import { useState } from 'react';
import { Pencil, Save, Sparkles, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Input from '../ui/Input';
import Card from '../ui/Card';

const BUSINESS_DOMAINS = [
  'Finance',
  'Supply Chain Management',
  'Operations',
  'Customer Experience',
  'Human Resources',
  'Sales & Marketing',
  'Information Technology',
  'Research & Development',
  'Manufacturing',
  'Healthcare',
  'Retail & E-commerce',
  'Telecommunications',
];

interface BusinessDomainFormProps {
  domain?: string;
  context?: string;
  onDomainChange?: (value: string) => void;
  onContextChange?: (value: string) => void;
}

export default function BusinessDomainForm({
  domain = '',
  context = '',
  onDomainChange,
  onContextChange
}: BusinessDomainFormProps) {
  const [isEditing, setIsEditing] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiFeedback, setAiFeedback] = useState<{
    type: 'suggestion' | 'clarification' | 'success';
    message: string;
  } | null>(null);

  const filteredDomains = BUSINESS_DOMAINS.filter(d =>
    d.toLowerCase().includes(domain.toLowerCase())
  );

  const handleDomainSelect = (selectedDomain: string) => {
    onDomainChange?.(selectedDomain);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-6">
      {/* Business Domain Selection */}
      <div className="relative">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Business Domain <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Input
            value={domain}
            onChange={(e) => {
              onDomainChange?.(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Select or type a business domain..."
            helperText="Choose from common domains or enter a custom one"
            required
          />
          {showSuggestions && filteredDomains.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {filteredDomains.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => handleDomainSelect(d)}
                  className="w-full text-left px-4 py-2 hover:bg-primary-50 transition-colors text-sm text-neutral-700 hover:text-primary-700"
                >
                  {d}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Business Domain Context */}
      <div>
        <Textarea
          label="Business Domain Context"
          value={context}
          onChange={(e) => onContextChange?.(e.target.value)}
          placeholder="Describe the project scope, objectives, and key challenges...

Example:
• Project Scope: Build a customer 360 view integrating data from multiple systems
• Business Objective: Improve customer retention and personalization
• Key Challenges: Data silos across CRM, ERP, and e-commerce platforms
• Timeline: 3-month implementation
• Key Stakeholders: CEO, CMO, Head of Data Analytics
• Expected Outcomes: Real-time customer insights, improved cross-sell opportunities"
          rows={10}
          className="font-mono text-sm"
          helperText="Provide detailed context about the project scope and business objectives. This helps generate a more tailored graph solution."
          required
        />
      </div>

      {/* Helper Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for better results:</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Be specific about your industry and business model</li>
          <li>Describe the key data entities and relationships you want to model</li>
          <li>Mention any specific use cases or queries you need to support</li>
          <li>Include information about data volume and performance requirements</li>
        </ul>
      </div>
    </div>
  );
}
